import { Button, Checkbox, Modal, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  errorMessage,
  listSkillsOptions,
  listSpaceConnectorsQueryKey,
  type SpaceConnectorResponse,
  setSpaceConnectorSkillsMutation,
  updateSpaceConnectorMutation,
} from "@/api";
import { Text } from "@/components/Text";
import { connectorName } from "@/features/build/connectors/connectorName";
import { SettingValues } from "./SettingValues";

/** This space's values for a connector's settings, and the skills that explain how the space uses it. */
export function SpaceConnectorModal({
  spaceUuid,
  binding,
  onClose,
}: Readonly<{ spaceUuid: string; binding: SpaceConnectorResponse; onClose: () => void }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.connectors" });
  const { t: tc } = useTranslation("connectors");
  const toast = useToast();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>(binding.config ?? {});
  const attached = (binding.skills ?? []).map((s) => s.uuid);
  const [skillUuids, setSkillUuids] = useState<string[]>(attached);
  // Skills I can attach: platform skills, and ones I admin (attaching shares them in the space).
  const skills = useQuery({ ...listSkillsOptions({ query: { page_size: 100 } }), select: (r) => r.data });
  const choices = (skills.data ?? []).filter(
    (s) => s.isSystem || s.currentUserRole === "admin" || attached.includes(s.uuid),
  );
  const path = { space_uuid: spaceUuid, binding_uuid: binding.uuid };
  const update = useMutation(updateSpaceConnectorMutation());
  const setSkills = useMutation(setSpaceConnectorSkillsMutation());
  const pending = update.isPending || setSkills.isPending;
  const close = useCallback(() => !pending && onClose(), [pending, onClose]);

  const save = async () => {
    try {
      await update.mutateAsync({ path, body: { config: values }, throwOnError: true });
      await setSkills.mutateAsync({ path, body: { skillUuids }, throwOnError: true });
      await queryClient.invalidateQueries({
        queryKey: listSpaceConnectorsQueryKey({ path: { space_uuid: spaceUuid } }),
      });
      toast.success(t("saved"));
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, t("failed")));
    }
  };

  return (
    <Modal
      open
      onClose={close}
      title={t("editTitle", { name: connectorName(binding.connector, tc) })}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            {t("cancel")}
          </Button>
          <Button variant="primary" loading={pending} onClick={() => void save()}>
            {t("save")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <SettingValues
          settings={binding.settings ?? []}
          values={values}
          onChange={(name, value) => setValues((v) => ({ ...v, [name]: value }))}
        />
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1">
            <Text as="span" variant="body-sm">
              {t("skills")}
            </Text>
          </legend>
          <Text variant="caption" tone="tertiary">
            {t("skillsHint")}
          </Text>
          {skills.isSuccess && choices.length === 0 && (
            <Text variant="body-sm" tone="tertiary">
              {t("noSkills")}
            </Text>
          )}
          <div className="flex max-h-60 flex-col gap-2 overflow-y-auto">
            {choices.map((skill) => (
              <Checkbox
                key={skill.uuid}
                label={skill.name}
                description={skill.description ?? undefined}
                checked={skillUuids.includes(skill.uuid)}
                onChange={(on) =>
                  setSkillUuids((list) => (on ? [...list, skill.uuid] : list.filter((u) => u !== skill.uuid)))
                }
              />
            ))}
          </div>
        </fieldset>
      </div>
    </Modal>
  );
}
